import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { supabase } from '@/shared/integrations/supabase/client';
import { captureEdgeFailure } from '@/shared/integrations/sentry/reportEdgeFailure';

// Singleton state
let loadPromise: Promise<void> | null = null;
let cachedApiKey: string | null = null;
let isLoaded = false;
let optionsSet = false;

/**
 * Load Google Maps API using Google's official loader
 * - Singleton pattern: never loads script twice
 * - API key caching: only fetches once
 * - 8-second timeout protection
 * - Error recovery: resets on failure for retry
 */
export async function loadGoogleMaps(): Promise<void> {
  // Return immediately if already loaded
  if (isLoaded && window.google?.maps) {
    return;
  }
  
  // Return existing promise if currently loading
  if (loadPromise) {
    return loadPromise;
  }
  
  loadPromise = (async () => {
    try {
      // 1. Get API key (cached after first call)
      if (!cachedApiKey) {
        const { data, error } = await supabase.functions.invoke('get-google-maps-key');

        if (error || !data?.apiKey) {
          console.error('[GoogleMaps] Failed to get API key:', error);
          captureEdgeFailure({
            endpoint: 'get-google-maps-key',
            error: error ?? 'response missing apiKey',
          });
          throw new Error('Failed to get Google Maps API key');
        }

        cachedApiKey = data.apiKey;
      }

      // 2. Set options once (must be before first importLibrary call)
      if (!optionsSet) {
        setOptions({
          key: cachedApiKey ?? undefined,
          v: 'weekly',
          libraries: ['marker', 'places']
        });
        optionsSet = true;
      }

      // 3. Load with timeout (8 seconds max)
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Google Maps loading timeout (8s)')), 8000)
      );
      
      // Import the maps library to trigger the load
      await Promise.race([
        importLibrary('maps'),
        timeoutPromise
      ]);
      
      isLoaded = true;
      
    } catch (error) {
      // Reset so retry is possible
      console.error('[GoogleMaps] Load failed:', error);
      loadPromise = null;
      throw error;
    }
  })();
  
  return loadPromise;
}

/**
 * Check if Google Maps is already loaded
 */
export function isGoogleMapsLoaded(): boolean {
  return isLoaded && !!window.google?.maps;
}

/**
 * Preload Google Maps (non-blocking, fire and forget)
 */
export function preloadGoogleMaps(): void {
  loadGoogleMaps().catch(() => {
    // Silently fail - will retry when component mounts
  });
}
